import React, { useState } from 'react';

/**
 * Indicador visual de saúde por-item (monitor/automação/estratégia).
 * Não mede lucratividade — sinaliza estados quebrados/parados/inconsistentes.
 * Clique copia o texto do motivo (tooltip) pra área de transferência.
 *
 * Convenção de cores (eixo saúde + eixo estado):
 *  - vermelho (error): índice referenciado não existe no cache
 *  - amarelo  (warn):  índice existe mas está parado/desatualizado
 *  - verde    (ok):    saudável e armada, esperando condições
 *  - azul     (fired): todas as condições atendidas — disparando agora
 *  - cinza    (idle):  automação inativa
 *
 * props:
 * - health: { status: 'ok'|'fired'|'warn'|'error'|'idle', reasons: string[] } | undefined
 */
const STATUS = {
    ok:    { color: '#22c55e', label: 'OK' },
    fired: { color: '#3b82f6', label: 'Disparando' },
    warn:  { color: '#f59e0b', label: 'Atenção' },
    error: { color: '#ef4444', label: 'Problema' },
    idle:  { color: '#9ca3af', label: 'Parado' }
};

function fallbackCopy(text, done) {
    try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        done();
    } catch (_) { /* sem clipboard disponível */ }
}

function HealthDot({ health }) {
    const [copied, setCopied] = useState(false);

    const status = (health && health.status) || 'idle';
    const cfg = STATUS[status] || STATUS.idle;
    const reasons = (health && Array.isArray(health.reasons) && health.reasons.length)
        ? health.reasons.join('\n')
        : cfg.label;

    function copyReasons(e) {
        e.stopPropagation(); // não disparar ação da linha (ex.: abrir edição)
        const done = () => { setCopied(true); setTimeout(() => setCopied(false), 1200); };
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(reasons).then(done).catch(() => fallbackCopy(reasons, done));
        } else {
            fallbackCopy(reasons, done);
        }
    }

    // Wrapper de 22px amplia a área de hover/clique; o ponto visível fica em 10px.
    return (
        <span
            title={reasons}
            aria-label={`Saúde: ${cfg.label} (clique para copiar)`}
            onClick={copyReasons}
            style={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 22,
                height: 22,
                marginRight: 4,
                cursor: 'pointer',
                verticalAlign: 'middle',
                flexShrink: 0
            }}
        >
            <span
                style={{
                    display: 'inline-block',
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: cfg.color
                }}
            />
            {copied && (
                <span
                    style={{
                        position: 'absolute',
                        left: '100%',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        marginLeft: 4,
                        whiteSpace: 'nowrap',
                        fontSize: 10,
                        fontWeight: 600,
                        color: '#16a34a',
                        pointerEvents: 'none'
                    }}
                >
                    copiado!
                </span>
            )}
        </span>
    );
}

export default HealthDot;
